import { AgileRecommendationInput, AgileRecommendationTypes } from '@components/agile/types/AgileRecommendationTypes';
import ImageCellRenderer from '@components/agile/agileComponents/agGridCommonComponents/ImageCellRenderer';
import QuantityButtonCellRenderer from '@components/agile/agileComponents/agGridCommonComponents/QuantityButtonCellRenderer';
import diffCellStyleRenderer from '@components/agile/agileComponents/agGridCommonComponents/DiffCellStyleRenderer';
import '../ZoneMap.css';

export const colDefs = [
  {
    headerName: 'ASIN',
    field: 'asin',
    pinned: true,
  },
  {
    headerName: 'Title',
    field: 'title',
    editable: false,
    pinned: true,
  },

  {
    field: 'image',
    editable: false,
    cellRenderer: ImageCellRenderer,
    filter: false,
    sortable: false,
    pinned: true,
  },
  {
    headerName: 'Facings',
    field: 'remainingFacings',
    cellRenderer: QuantityButtonCellRenderer,
    flex: 2,
    minWidth: 180,
    maxWidth: 180,
    filter: false,
    pinned: true,
  },
  {
    headerName: 'Diff',
    field: 'difference',
    cellRenderer: diffCellStyleRenderer,
    filter: false,
    pinned: true,
    width: 80,
    maxWidth: 100,
  },
  {
    headerName: 'Total FOH Capacity (Units)',
    wrapHeaderText: true,
    headerClass: 'text-center',
    field: 'remainingCapacity',
    editable: false,
    minWidth: 160,
    filter: false,
  },
  {
    headerName: 'UPHL1',
    field: 'uphl1',
    editable: false,
  },
  {
    headerName: 'UPHL2',
    field: 'uphl2',
    editable: false,
  },
  {
    headerName: 'UPHL3',
    field: 'uphl3',
    editable: false,
  },
  {
    headerName: 'Shrink Cost (-1W)',
    field: 'shrinkCost',
    editable: false,
    filter: true,
    wrapHeaderText: true,
    minWidth: 160,
  },
  {
    headerName: 'Shrink % Ops (-1W) %',
    field: 'shrinkPerOps',
    editable: false,
    filter: true,
    minWidth: 160,
    wrapHeaderText: true,
  },
  {
    headerName: 'Shrink % Ops (-13W) %',
    field: 'shrinkPerOps13week',
    editable: false,
    filter: true,
    minWidth: 160,
    wrapHeaderText: true,
  },
  {
    headerName: 'Shrink % Ops (-26W) %',
    field: 'shrinkPerOps26week',
    editable: false,
    filter: true,
    minWidth: 160,
    wrapHeaderText: true,
  },
  {
    headerName: 'Shrink % Ops (-52W) %',
    field: 'shrinkPerOps52week',
    editable: false,
    filter: true,
    minWidth: 160,
    wrapHeaderText: true,
  },
  {
    headerName: 'Moving Avg Sales (-13W) $',
    field: 'movingAvgSales13week',
    editable: false,
    filter: true,
    minWidth: 160,
    wrapHeaderText: true,
  },
  {
    headerName: 'Moving Avg Sales (-26W) $',
    field: 'movingAvgSales26week',
    editable: false,
    filter: true,
    minWidth: 160,
    wrapHeaderText: true,
  },
  {
    headerName: 'Moving Avg Sales (-52W) $',
    field: 'movingAvgSales52week',
    editable: false,
    filter: true,
    minWidth: 160,
    wrapHeaderText: true,
  },
];

export function calculateSum(array: AgileRecommendationTypes[], property: unknown) {
  return array.reduce(
    (accumulator, obj) =>
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      accumulator + parseInt(String(obj[property]), 10),
    0,
  );
}

export function calculateUnitsToRemove(
  totalNewFAcings: number,
  maxFacingUnits: number,
  occupieFacingUnits: number,
  diff: number,
) {
  const remove = totalNewFAcings - (maxFacingUnits - occupieFacingUnits) + diff;
  if (remove <= 0) {
    return 0;
  }
  return remove;
}

/** Calculate total new facing units in zone after
 adding new facings and removing existing facing from recommendation. * */
export function CalculateNewOccupiedFacings(totalNewFAcings: number, occupieFacingUnits: number, diff: number) {
  const remove = totalNewFAcings + occupieFacingUnits + diff;
  if (remove <= 0) {
    return 0;
  }
  return remove;
}

/** This function will take new asin List and recommendation List as input and
 will modify recommended asin if same asin already exist in new asin list.
 It will also return list of duplicate asins which will help to filter
 duplicate asins from new list of asins
 e.g Input : newAsinList = [{asin: 'asin1', facingUnits: 2},{asin: 'asin2', facingUnits: 2}]
            recommendedList = [{asin: 'asin1', remainingFacings: 2, remainingCapacity: 2}]
 output : asinsToUpdate = [{asin: 'asin1', remainingFacings: 4, remainingCapacity: 4}]
          duplicateAsinsList = ['asin1']
 * */

export function mergeDuplicateProductChanges(
  recommendationInput: AgileRecommendationInput[],
  recommendedList: AgileRecommendationTypes[],
): {
  uniqueRecommendedAsinList: AgileRecommendationTypes[];
  uniqueNewAsinList: AgileRecommendationInput[];
} {
  const duplicateAsinsList: string[] = [];
  const uniqueRecommendedAsinList: AgileRecommendationTypes[] = [];
  recommendedList.forEach((recommendedEntry) => {
    const newAsin: AgileRecommendationInput | undefined = recommendationInput.find(
      (inputEntry) => recommendedEntry.asin === inputEntry.asin,
    );

    if (newAsin) {
      const newTotalFacing = +recommendedEntry.remainingFacings + +newAsin.facingUnits;
      uniqueRecommendedAsinList.push({
        ...recommendedEntry,
        remainingFacings: newTotalFacing,
        remainingCapacity: recommendedEntry.remainingCapacity + newAsin.fohCapacity,
        difference: +recommendedEntry.difference + +newAsin.facingUnits,
      });
      duplicateAsinsList.push(newAsin.asin);
    } else {
      uniqueRecommendedAsinList.push(recommendedEntry);
    }
  });
  const uniqueNewAsinList: AgileRecommendationInput[] = recommendationInput.filter(
    (item) => !duplicateAsinsList.includes(item.asin),
  );
  return { uniqueRecommendedAsinList, uniqueNewAsinList };
}
